import React, { Component } from 'react'
import {
    Alert, Button, Card, Col, Descriptions, Form, Input, InputNumber, message, Modal, Progress,
    Row, Select, Space, Switch, Table, Tag, Typography, Upload
} from 'antd'
import { CloudUploadOutlined, FileTextOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import {
    deleteA2aModel, disableA2aModel, disableCodexPlusRelease, enableA2aModel, getA2aReleaseOverview,
    publishCodexPlusRelease, saveA2aModel, saveA2aPrompt, saveCodexPlusRelease, uploadCodexPlusAsset,
} from '../../request'
import './index.less'

const { TextArea } = Input
const { Text, Paragraph } = Typography
const { Option } = Select

const PLATFORM_META = [
    { value: 'macos-arm64', title: 'macOS Apple 芯片', accept: '.dmg' },
    { value: 'macos-x64', title: 'macOS Intel', accept: '.dmg' },
    { value: 'windows-x64', title: 'Windows x64', accept: '.exe,.msi' },
]
const STATUS_META = {
    draft: { text: '草稿', color: 'default' },
    published: { text: '已发布', color: 'success' },
    disabled: { text: '已停用', color: 'error' },
}

const humanSize = value => {
    const size = Number(value || 0)
    if (!size) return '-'
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
    return `${(size / 1024 / 1024).toFixed(1)} MB`
}

export default class A2aManagement extends Component {
    releaseFormRef = React.createRef()
    modelFormRef = React.createRef()
    releaseNotesInputRef = React.createRef()
    releaseNotesTarget = null

    state = {
        loading: false,
        error: '',
        prompt: '',
        savedPrompt: null,
        promptSaving: false,
        releases: [],
        models: [],
        releaseVisible: false,
        releaseSaving: false,
        editingRelease: null,
        modelVisible: false,
        modelSaving: false,
        editingModel: null,
        modelActionId: null,
        uploadProgress: {},
        actionReleaseId: null,
        releaseNotesSavingId: null,
    }

    componentDidMount() {
        this.loadOverview()
    }

    loadOverview = () => {
        this.setState({ loading: true, error: '' })
        return getA2aReleaseOverview().then(result => {
            const overview = result || {}
            const prompt = overview.prompt || null
            this.setState({
                savedPrompt: prompt,
                prompt: prompt ? prompt.promptContent || '' : '',
                releases: overview.releases || [],
                models: overview.models || [],
            })
        }).catch(error => {
            this.setState({ error: error && error.message ? error.message : 'A2A管理数据加载失败' })
        }).finally(() => this.setState({ loading: false }))
    }

    savePrompt = () => {
        const prompt = this.state.prompt.trim()
        if (!prompt) return message.warning('请先填写任务发送方 Prompt')
        this.setState({ promptSaving: true })
        saveA2aPrompt({ prompt }).then(result => {
            // 2026-08-08 coder(lq): Prompt 只保留当前值，保存后 Portal 立即生效。
            message.success('Prompt 已保存并立即生效')
            this.setState({ savedPrompt: result })
        }).catch(error => this.setState({ error: error.message || 'Prompt 保存失败' }))
            .finally(() => this.setState({ promptSaving: false }))
    }

    openRelease = (release = null) => {
        // 2026-08-08 coder(lq): 弹窗使用 destroyOnClose，打开时不能在 Form 挂载前访问 ref，否则会导致页面白屏。
        this.setState({
            releaseVisible: true,
            editingRelease: release,
        })
    }

    saveRelease = () => {
        const form = this.releaseFormRef.current
        if (!form) {
            message.error('版本表单尚未准备完成，请重新打开后再试')
            return
        }
        form.validateFields().then(values => {
            this.setState({ releaseSaving: true })
            return saveCodexPlusRelease({
                id: this.state.editingRelease ? this.state.editingRelease.id : undefined,
                ...values,
            }).then(result => {
                message.success('版本草稿已保存，请继续上传安装包')
                this.setState({ releaseVisible: false, editingRelease: result })
                return this.loadOverview()
            }).catch(error => this.setState({ error: error.message || '版本草稿保存失败' }))
                .finally(() => this.setState({ releaseSaving: false }))
        })
    }

    openModel = (model = null) => {
        this.setState({ modelVisible: true, editingModel: model })
    }

    saveModel = () => {
        const form = this.modelFormRef.current
        if (!form) {
            message.error('模型表单尚未准备完成，请重新打开后再试')
            return
        }
        form.validateFields().then(values => {
            this.setState({ modelSaving: true })
            return saveA2aModel({
                id: this.state.editingModel ? this.state.editingModel.id : undefined,
                modelCode: values.modelCode,
                modelName: values.modelName,
                sortOrder: values.sortOrder,
                enabled: values.enabled ? 1 : 0,
            }).then(() => {
                message.success(this.state.editingModel ? '模型已更新' : '模型已新增')
                this.setState({ modelVisible: false, editingModel: null })
                return this.loadOverview()
            }).catch(error => this.setState({ error: error.message || '模型保存失败' }))
                .finally(() => this.setState({ modelSaving: false }))
        })
    }

    toggleModelEnabled = (model, enabled) => {
        this.setState({ modelActionId: model.id })
        const request = enabled ? enableA2aModel : disableA2aModel
        return request({ id: model.id }).then(() => {
            message.success(enabled ? '模型已启用' : '模型已停用')
            return this.loadOverview()
        }).catch(error => this.setState({ error: error.message || '模型状态更新失败' }))
            .finally(() => this.setState({ modelActionId: null }))
    }

    confirmDeleteModel = model => {
        Modal.confirm({
            title: `删除模型 ${model.modelCode}？`,
            content: '删除后 CodexPlus 客户端将不再展示该模型。若只是临时下线，建议使用停用。',
            okText: '确认删除',
            okButtonProps: { danger: true },
            onOk: () => {
                this.setState({ modelActionId: model.id })
                return deleteA2aModel({ id: model.id }).then(() => {
                    message.success('模型已删除')
                    return this.loadOverview()
                }).catch(error => {
                    this.setState({ error: error.message || '模型删除失败' })
                    throw error
                }).finally(() => this.setState({ modelActionId: null }))
            },
        })
    }

    importReleaseNotes = release => {
        if (release.status !== 'draft') {
            message.info('已发布版本的版本说明不可修改，请新建版本后再发布')
            return
        }
        this.releaseNotesTarget = release
        this.releaseNotesInputRef.current?.click()
    }

    readReleaseNotesFile = file => new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = event => resolve(String(event.target?.result || ''))
        reader.onerror = () => reject(new Error('版本说明文件读取失败'))
        reader.readAsText(file, 'utf-8')
    })

    handleReleaseNotesFileChange = event => {
        const file = event.target.files?.[0]
        const release = this.releaseNotesTarget
        event.target.value = ''
        if (!file || !release) return
        if (!/\.md$/i.test(file.name || '')) {
            message.warning('请选择 .md 格式的版本说明文件')
            return
        }
        if (file.size > 512 * 1024) {
            message.warning('版本说明文件不能超过 512 KB')
            return
        }
        this.readReleaseNotesFile(file).then(releaseNotes => {
            if (!releaseNotes.trim()) {
                message.warning('版本说明文件不能为空')
                return
            }
            this.setState({ releaseNotesSavingId: release.id, error: '' })
            return saveCodexPlusRelease({
                id: release.id,
                version: release.version,
                channel: release.channel,
                releaseNotes: releaseNotes.trim(),
            }).then(() => {
                message.success('版本说明已导入')
                return this.loadOverview()
            })
        }).catch(error => this.setState({ error: error.message || '版本说明保存失败' }))
            .finally(() => this.setState({ releaseNotesSavingId: null }))
    }

    uploadAsset = (release, platform, file) => {
        if (!String(release.releaseNotes || '').trim()) {
            message.warning({
                content: '当前版本还没有 RELEASE_NOTES.md。它用于在 CodexPlus 更新弹窗中展示本次更新内容；安装包会继续上传。',
                duration: 6,
            })
        }
        const key = `${release.id}-${platform}`
        this.setState(state => ({ uploadProgress: { ...state.uploadProgress, [key]: 1 }, error: '' }))
        uploadCodexPlusAsset(release.id, platform, file, event => {
            const percent = event.total ? Math.round(event.loaded * 100 / event.total) : 0
            this.setState(state => ({ uploadProgress: { ...state.uploadProgress, [key]: percent } }))
        }).then(() => {
            const platformTitle = PLATFORM_META.find(item => item.value === platform).title
            message.success(release.status === 'published'
                ? `${platformTitle} 安装包已补充并立即生效`
                : `${platformTitle} 安装包上传成功`)
            return this.loadOverview()
        }).catch(error => this.setState({ error: error.message || '安装包上传失败' }))
            .finally(() => this.setState(state => {
                const progress = { ...state.uploadProgress }
                delete progress[key]
                return { uploadProgress: progress }
            }))
        return false
    }

    confirmPublishRelease = release => {
        const republish = release.status === 'disabled'
        const notesWarning = !String(release.releaseNotes || '').trim()
        Modal.confirm({
            title: `确认${republish ? '重新' : ''}发布 CodexPlus ${release.version}？`,
            content: <div>
                <p>发布到 {release.channel === 'beta' ? 'Beta' : '稳定'} 通道后，Portal 会按平台使用包含对应安装包的最新版本；旧版本会保留，供本版本未包含的平台继续下载。</p>
                {notesWarning && <p className="a2a-release-notes-warning">当前尚未配置 RELEASE_NOTES.md，发布后 CodexPlus 更新弹窗不会展示本次更新内容。</p>}
            </div>,
            okText: republish ? '确认重新发布' : '确认发布',
            onOk: () => {
                this.setState({ actionReleaseId: release.id })
                return publishCodexPlusRelease({ id: release.id }).then(() => {
                    message.success('CodexPlus 版本已发布')
                    return this.loadOverview()
                }).catch(error => {
                    this.setState({ error: error.message || '版本发布失败' })
                    throw error
                }).finally(() => this.setState({ actionReleaseId: null }))
            },
        })
    }

    confirmDisableRelease = release => {
        Modal.confirm({
            title: `停用 CodexPlus ${release.version}？`,
            content: '停用后客户端与 Portal 都不会再获取到这个版本。',
            okText: '确认停用',
            okButtonProps: { danger: true },
            onOk: () => disableCodexPlusRelease({ id: release.id }).then(() => {
                message.success('版本已停用')
                return this.loadOverview()
            }),
        })
    }

    renderAssets = release => {
        const assets = release.assets || []
        const notesConfigured = Boolean(String(release.releaseNotes || '').trim())
        return <div className="a2a-release-assets">
            <div className={`a2a-release-notes-upload ${notesConfigured ? 'is-configured' : 'is-missing'}`}>
                <div className="a2a-release-notes-copy">
                    <div className="a2a-release-notes-title"><FileTextOutlined /> 版本说明（RELEASE_NOTES.md）</div>
                    <div className="a2a-release-notes-help">用于在 CodexPlus 更新弹窗中展示本次新增功能、优化内容和问题修复；这里只保存文字，不会作为安装包下载。</div>
                </div>
                <div className="a2a-release-notes-action">
                    <Tag color={notesConfigured ? 'green' : 'gold'}>{notesConfigured ? '已配置' : '未上传'}</Tag>
                    {release.status === 'draft' && <Button size="small" icon={<FileTextOutlined />} loading={this.state.releaseNotesSavingId === release.id} onClick={() => this.importReleaseNotes(release)}>
                        {notesConfigured ? '重新上传 MD' : '上传 MD'}
                    </Button>}
                </div>
            </div>
            <Row gutter={[12, 12]}>{PLATFORM_META.map(platform => {
            const asset = assets.find(item => item.platform === platform.value)
            const key = `${release.id}-${platform.value}`
            const progress = this.state.uploadProgress[key]
            const canUpload = release.status === 'draft' || (release.status === 'published' && !asset)
            return <Col xs={24} lg={8} key={platform.value}>
                <div className="a2a-asset-box">
                    <Text strong>{platform.title}</Text>
                    <Paragraph ellipsis={{ rows: 2 }} type={asset ? undefined : 'secondary'}>
                        {asset ? `${asset.fileName} · ${humanSize(asset.fileSize)}` : `未上传（${platform.accept}）`}
                    </Paragraph>
                    {progress !== undefined ? <Progress percent={progress} size="small" /> : canUpload ? (
                        <Upload accept={platform.accept} showUploadList={false}
                            beforeUpload={file => this.uploadAsset(release, platform.value, file)}>
                            <Button size="small" icon={<CloudUploadOutlined />}>
                                {release.status === 'published' ? '补充安装包' : (asset ? '替换安装包' : '上传安装包')}
                            </Button>
                        </Upload>
                    ) : asset ? <Tag color={release.status === 'published' ? 'green' : 'default'}>
                        {release.status === 'published' ? '已发布，不可替换' : '已停用，只读'}
                    </Tag> : <Text type="secondary">已停用，不可上传</Text>}
                    {asset && <Text className="a2a-sha" type="secondary">SHA-256：{asset.sha256}</Text>}
                </div>
            </Col>
            })}</Row>
        </div>
    }

    columns = [
        { title: '版本', dataIndex: 'version', width: 130, render: value => <Text strong>{value}</Text> },
        { title: '通道', dataIndex: 'channel', width: 90, render: value => <Tag color={value === 'beta' ? 'orange' : 'blue'}>{value}</Tag> },
        { title: '状态', dataIndex: 'status', width: 100, render: value => { const meta = STATUS_META[value] || {text:value}; return <Tag color={meta.color}>{meta.text}</Tag> } },
        { title: '安装包', key: 'assets', render: (_, release) => this.renderAssets(release) },
        { title: '版本说明', dataIndex: 'releaseNotes', width: 220, render: value => <Paragraph ellipsis={{rows:3}}>{value || '-'}</Paragraph> },
        { title: '操作', key: 'action', width: 170, fixed: 'right', render: (_, release) => <Space>
            {release.status === 'draft' && <Button type="link" size="small" onClick={() => this.openRelease(release)}>编辑</Button>}
            {release.status === 'draft' && <Button type="link" size="small" loading={this.state.actionReleaseId === release.id} onClick={() => this.confirmPublishRelease(release)}>发布</Button>}
            {release.status === 'disabled' && <Button type="link" size="small" loading={this.state.actionReleaseId === release.id} onClick={() => this.confirmPublishRelease(release)}>重新发布</Button>}
            {release.status === 'published' && <Button type="link" danger size="small" onClick={() => this.confirmDisableRelease(release)}>停用</Button>}
        </Space> },
    ]

    modelColumns = [
        { title: '模型标识', dataIndex: 'modelCode', width: 180, render: value => <Text code>{value}</Text> },
        { title: '展示名称', dataIndex: 'modelName', width: 180, render: value => value || '-' },
        { title: '排序', dataIndex: 'sortOrder', width: 90 },
        {
            title: '状态', dataIndex: 'enabled', width: 110,
            render: (value, model) => (
                <Switch
                    checkedChildren="启用"
                    unCheckedChildren="停用"
                    checked={Number(value) === 1}
                    loading={this.state.modelActionId === model.id}
                    onChange={checked => this.toggleModelEnabled(model, checked)}
                />
            ),
        },
        { title: '更新时间', dataIndex: 'updateTime', width: 180, render: value => value || '-' },
        {
            title: '操作', key: 'action', width: 150, fixed: 'right',
            render: (_, model) => <Space>
                <Button type="link" size="small" onClick={() => this.openModel(model)}>编辑</Button>
                <Button type="link" danger size="small" loading={this.state.modelActionId === model.id}
                    onClick={() => this.confirmDeleteModel(model)}>删除</Button>
            </Space>,
        },
    ];

    render() {
        const { loading, error, savedPrompt, editingModel } = this.state
        return <div className="a2a-management">
            <div className="a2a-page-head">
                <div>
                    <h2>A2A 管理</h2>
                    <Text type="secondary">集中管理任务发送说明、CodexPlus 安装包、自动更新版本与模型白名单。</Text>
                </div>
                <Button icon={<ReloadOutlined />} loading={loading} onClick={this.loadOverview}>刷新</Button>
            </div>
            {error && <Alert closable type="error" showIcon message="操作没有完成" description={error} onClose={() => this.setState({error:''})} />}

            <Card title="任务发送方 Prompt" loading={loading}>
                <Alert type="info" showIcon message="这段内容会显示给需要从服务端派发 A2A 任务的用户"
                    description="可以写清 MCP 安装、环境变量设置、任务字段和发送示例。保存后 Portal A2A 页面立即使用新内容。" />
                <Descriptions size="small" column={1} className="a2a-meta">
                    <Descriptions.Item label="当前状态">{savedPrompt ? `已生效 · ${savedPrompt.updateTime || '-'}` : '尚未配置'}</Descriptions.Item>
                </Descriptions>
                <TextArea value={this.state.prompt} rows={16} onChange={event => this.setState({prompt:event.target.value})}
                    placeholder="填写任务发送方从安装 MCP 到发送任务的完整说明……" />
                <Space className="a2a-actions">
                    <Button type="primary" loading={this.state.promptSaving} onClick={this.savePrompt}>保存并立即生效</Button>
                </Space>
            </Card>

            <Card title="模型白名单" loading={loading}
                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => this.openModel()}>新增模型</Button>}>
                <Alert type="info" showIcon message="控制 CodexPlus 可选用的模型"
                    description="仅「启用」状态的模型会通过 /a2a/client/models 下发给客户端。标识需与 Codex 实际模型 code 一致，例如 gpt-5.6-sol。" />
                <Table rowKey="id" columns={this.modelColumns} dataSource={this.state.models}
                    pagination={false} scroll={{ x: 900 }} className="a2a-model-table" style={{ marginTop: 12 }} />
            </Card>

            <Card title="CodexPlus 版本与安装包" loading={loading}
                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => this.openRelease()}>新建版本</Button>}>
                <Alert type="warning" showIcon message="发布后会用于客户端自动更新"
                    description="请至少上传一个平台安装包。已发布版本可以继续补充缺失平台，补充后立即生效；已有平台安装包不可替换，如需替换请升级版本号。稳定版与 Beta 版互不覆盖；安装包下载使用短时签名链接。" />
                <Table rowKey="id" columns={this.columns} dataSource={this.state.releases} pagination={false}
                    scroll={{x:1200}} className="a2a-release-table" />
            </Card>

            <Modal title={this.state.editingRelease ? '编辑版本草稿' : '新建版本草稿'} visible={this.state.releaseVisible}
                okText="保存草稿" cancelText="取消" confirmLoading={this.state.releaseSaving} onOk={this.saveRelease}
                onCancel={() => this.setState({releaseVisible:false})} destroyOnClose>
                <Form
                    key={this.state.editingRelease ? `edit-${this.state.editingRelease.id}` : 'new'}
                    ref={this.releaseFormRef}
                    layout="vertical"
                    initialValues={{
                        version: this.state.editingRelease ? this.state.editingRelease.version : '',
                        channel: this.state.editingRelease ? this.state.editingRelease.channel : 'stable',
                        releaseNotes: this.state.editingRelease ? this.state.editingRelease.releaseNotes : '',
                    }}
                >
                    <Form.Item name="version" label="版本号" rules={[{required:true,message:'请填写版本号'},{pattern:/^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/,message:'例如 1.2.3 或 1.2.3-beta.1'}]}>
                        <Input placeholder="例如 1.2.3" />
                    </Form.Item>
                    <Form.Item name="channel" label="发布通道" rules={[{required:true}]}>
                        <Select><Option value="stable">稳定版 stable</Option><Option value="beta">测试版 beta</Option></Select>
                    </Form.Item>
                    <Form.Item name="releaseNotes" label="版本说明"><TextArea rows={6} placeholder="说明本次新增功能、优化内容和问题修复；也可以在保存草稿后从安装包区域导入 RELEASE_NOTES.md" /></Form.Item>
                </Form>
            </Modal>
            <Modal title={editingModel ? '编辑模型' : '新增模型'} visible={this.state.modelVisible}
                okText="保存" cancelText="取消" confirmLoading={this.state.modelSaving} onOk={this.saveModel}
                onCancel={() => this.setState({ modelVisible: false, editingModel: null })} destroyOnClose>
                <Form
                    key={editingModel ? `model-edit-${editingModel.id}` : 'model-new'}
                    ref={this.modelFormRef}
                    layout="vertical"
                    initialValues={{
                        modelCode: editingModel ? editingModel.modelCode : '',
                        modelName: editingModel ? (editingModel.modelName || '') : '',
                        sortOrder: editingModel ? (editingModel.sortOrder || 0) : 0,
                        enabled: editingModel ? Number(editingModel.enabled) === 1 : true,
                    }}
                >
                    <Form.Item name="modelCode" label="模型标识"
                        rules={[
                            { required: true, message: '请填写模型标识' },
                            { pattern: /^[0-9A-Za-z][0-9A-Za-z._+-]{0,190}$/, message: '仅支持字母数字及 . _ + -' },
                        ]}>
                        <Input placeholder="例如 gpt-5.6-sol" />
                    </Form.Item>
                    <Form.Item name="modelName" label="展示名称">
                        <Input placeholder="例如 GPT-5.6 Sol（可空，客户端可用标识兜底）" />
                    </Form.Item>
                    <Form.Item name="sortOrder" label="排序（越小越靠前）" rules={[{ required: true, message: '请填写排序' }]}>
                        <InputNumber style={{ width: '100%' }} min={0} max={999999} />
                    </Form.Item>
                    <Form.Item name="enabled" label="启用状态" valuePropName="checked">
                        <Switch checkedChildren="启用" unCheckedChildren="停用" />
                    </Form.Item>
                </Form>
            </Modal>
            <input ref={this.releaseNotesInputRef} className="a2a-release-notes-input" type="file" accept=".md,text/markdown" onChange={this.handleReleaseNotesFileChange} />
        </div>
    }
}
